import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { PlaylistAdd as PlaylistAddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import configuracionService from '../../services/configuracionService';

interface CompletarConfiguracionBaseButtonProps {
  onCompleted?: () => void;
}

/**
 * Ejecuta a demanda el completado de Configuraciones y Variables del sistema (Varsis2)
 * con los valores base. No pisa valores existentes.
 */
const CompletarConfiguracionBaseButton: React.FC<CompletarConfiguracionBaseButtonProps> = ({ onCompleted }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const handleConfirm = async () => {
    try {
      setRunning(true);
      const res = await configuracionService.completarBase();
      const partes = [
        res.configuracionesAgregadas > 0
          ? `${res.configuracionesAgregadas} parámetro(s) agregado(s)`
          : 'No faltaban parámetros',
      ];
      if (res.varsis2Creada) partes.push('se creó el registro de variables del sistema');
      enqueueSnackbar(`Valores base completados: ${partes.join(', ')}.`, { variant: 'success' });
      setOpen(false);
      onCompleted?.();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      console.error('Error al completar valores base:', error);
      enqueueSnackbar(
        status === 403
          ? 'Solo un administrador puede completar los valores base.'
          : 'Error al completar los valores base',
        { variant: 'error' },
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<PlaylistAddIcon />}
        onClick={() => setOpen(true)}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Completar valores base
      </Button>
      <Dialog open={open} onClose={() => !running && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Completar valores base</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Se agregarán los parámetros del sistema que falten y se completarán las variables del sistema vacías
            con los valores base. Úselo después de una limpieza del tenant o si faltan datos.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Los valores ya cargados no se modifican. No se copian datos propios de la empresa (correo SMTP,
            credenciales, contraseñas ni cliente predeterminado) y los módulos quedan desactivados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit" disabled={running}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={running}
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {running ? 'Completando...' : 'Completar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompletarConfiguracionBaseButton;
